import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useQuery } from '@tanstack/react-query';
import { Link as EnlaceRuta, useLocation } from 'react-router-dom';
import type { Modulo, Permiso } from '@exp/contracts';
import { pacientes } from '../api/recursos';
import { useSesion } from '../sesion/SesionProvider';
import { Dato, fechaLegible } from './comunes';
import { iniciales } from './iniciales';
import { CHASIS } from './tokens';

/**
 * Pestanas del expediente. Cada una se ensena solo a quien puede leerla y, si
 * es de una especialidad, al consultorio que la tiene contratada.
 */
export const PESTANAS_DEL_EXPEDIENTE: ReadonlyArray<{
  ruta: string;
  texto: string;
  permisos: readonly Permiso[];
  modulo?: Modulo;
}> = [
  { ruta: '', texto: 'Resumen', permisos: ['pacientes:leer'] },
  { ruta: 'historia', texto: 'Historia clínica', permisos: ['historia:leer'] },
  {
    ruta: 'atencion',
    texto: 'Atención',
    permisos: ['encuentros:leer', 'observaciones:leer', 'antecedentes:leer', 'documentos:leer'],
  },
  {
    ruta: 'estudios',
    texto: 'Colposcopías',
    permisos: ['colposcopias:leer'],
    modulo: 'ginecologia-colposcopia',
  },
  { ruta: 'cardiologia', texto: 'Cardiología', permisos: ['estudios:leer'], modulo: 'cardiologia' },
  {
    ruta: 'oftalmologia',
    texto: 'Oftalmología',
    permisos: ['observaciones:leer', 'estudios:leer'],
    modulo: 'oftalmologia',
  },
  {
    ruta: 'otorrinolaringologia',
    texto: 'ORL',
    permisos: ['estudios:leer'],
    modulo: 'otorrinolaringologia',
  },
  {
    ruta: 'gastroenterologia',
    texto: 'Gastroenterología',
    permisos: ['estudios:leer'],
    modulo: 'gastroenterologia',
  },
  { ruta: 'planes', texto: 'Planes', permisos: ['planes:leer'], modulo: 'planes' },
  { ruta: 'documentos', texto: 'Recetas y notas', permisos: ['recetas:leer', 'notas:leer'] },
];

/**
 * Texto de alergias que NIEGA alergias («Ninguna conocida», «Niega»). Se
 * muestra tal cual, pero sin el rojo de alerta: pintar de rojo «ninguna»
 * ensena a ignorar el rojo.
 */
function niegaAlergias(texto: string): boolean {
  return /^\s*(ningun[ao]s?|niega|negad[ao]s?|sin alergias|no conocidas?|no refiere)\b/i.test(
    texto,
  );
}

/**
 * Identidad de la paciente, fija mientras se trabaja dentro de su expediente.
 *
 * Capturar un estudio en la paciente equivocada es el error mas caro que se
 * puede cometer en un expediente clinico; la forma de evitarlo es no dejar de
 * ver a quien se le esta escribiendo. Por eso la cinta acompana a TODAS las
 * pantallas de la paciente —tambien la captura y el detalle de un estudio— y
 * lleva las pestanas del expediente, para saltar entre ellas desde cualquiera.
 *
 * Las alergias viajan aqui, no dentro de una pestana: quien va a extender una
 * receta tiene que tropezarse con ellas sin ir a buscarlas.
 */
export function CintaDePaciente({ pacienteId }: { pacienteId: string }) {
  const { puede, tieneModulo } = useSesion();
  const { pathname } = useLocation();
  const consulta = useQuery({
    queryKey: ['paciente', pacienteId],
    queryFn: () => pacientes.detalle(pacienteId),
    enabled: Boolean(pacienteId),
    staleTime: 60_000,
  });

  const paciente = consulta.data;
  const base = `/pacientes/${pacienteId}`;
  const resto = pathname.slice(base.length).replace(/^\//, '');
  const pestanas = PESTANAS_DEL_EXPEDIENTE.filter(
    (p) => p.permisos.some((permiso) => puede(permiso)) && (!p.modulo || tieneModulo(p.modulo)),
  );
  // La captura y el detalle de un estudio viven bajo `estudios/`.
  const vigente =
    pestanas.find((p) => p.ruta !== '' && (resto === p.ruta || resto.startsWith(`${p.ruta}/`)))
      ?.ruta ?? (resto === '' ? '' : false);
  const editando = resto === 'editar';
  const enPestana = PESTANAS_DEL_EXPEDIENTE.some((p) => p.ruta === resto);

  return (
    <Box
      component="section"
      aria-label="Paciente"
      className="no-imprimir"
      sx={{
        position: { md: 'sticky' },
        top: 0,
        zIndex: 'var(--z-pegajoso)',
        // Los mismos margenes que el contenido: la tarjeta queda alineada con
        // las de abajo. Opaca: el contenido pasa por debajo al desplazarse.
        px: { xs: 'var(--space-5)', md: 'var(--space-7)', lg: 'var(--space-9)' },
        pt: { xs: 'var(--space-5)', md: 'var(--space-7)' },
        bgcolor: 'var(--bg-canvas)',
      }}
    >
      {/* Tarjeta del expediente, como la ficha de cliente de Telmo: avatar,
          nombre grande y las pestanas bajo un filete. */}
      <Box
        sx={{
          maxWidth: CHASIS.anchoTabla,
          mx: 'auto',
          px: 'var(--density-card-p)',
          pt: 'var(--density-card-p)',
          bgcolor: 'var(--bg-raised)',
          border: '1px solid var(--border-card)',
          borderRadius: 'var(--radius-card)',
          boxShadow: 'var(--shadow-1)',
        }}
      >
        <Stack direction="row" alignItems="flex-start" sx={{ gap: 'var(--space-4)' }}>
          <Tooltip title="Volver a pacientes">
            <IconButton
              component={EnlaceRuta}
              to="/pacientes"
              size="small"
              aria-label="Volver a pacientes"
              sx={{ flexShrink: 0, ml: '-6px', mt: { xs: '2px', md: '14px' } }}
            >
              <ArrowBackRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {paciente && (
            <Avatar
              aria-hidden
              sx={{
                display: { xs: 'none', sm: 'flex' },
                width: 56,
                height: 56,
                fontSize: '1.125rem',
                flexShrink: 0,
              }}
            >
              {iniciales(paciente.nombreCompleto)}
            </Avatar>
          )}

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            {consulta.isPending && (
              <Skeleton variant="text" width={280} height={30} sx={{ maxWidth: '70%' }} />
            )}
            {consulta.isError && (
              <Typography sx={{ color: 'var(--text-secondary)', py: '4px' }}>
                Expediente no disponible
              </Typography>
            )}
            {paciente && (
              <>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ columnGap: '12px' }}
                >
                  {/* Serif: la persona. Es el dato que no se pierde de vista. */}
                  <Typography
                    // Titulo de la pagina en las pestanas; en un estudio el
                    // titulo es el del documento y el nombre queda como contexto.
                    component={enPestana ? 'h1' : 'p'}
                    className="serif"
                    sx={{
                      fontSize: { xs: '1.375rem', md: '1.875rem' },
                      fontWeight: 600,
                      lineHeight: 1.15,
                      letterSpacing: 'var(--tracking-tight)',
                      color: 'var(--text-primary)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {paciente.nombreCompleto}
                  </Typography>
                  <Dato tono="tenue">Exp. {paciente.expediente}</Dato>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ columnGap: '12px', rowGap: '4px', mt: '4px' }}
                >
                  <Typography
                    variant="body2"
                    className="cifra"
                    sx={{ color: 'var(--text-secondary)' }}
                  >
                    {paciente.edad} años · {paciente.sexo} · nac.{' '}
                    {fechaLegible(paciente.fechaNacimiento)}
                    {paciente.telefono && (
                      <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>
                        {' · '}
                        <Dato tono="tenue">{paciente.telefono}</Dato>
                      </Box>
                    )}
                  </Typography>
                  {/* Alerta clinica: se lee por el icono y la palabra, no solo por el color. */}
                  {paciente.alergias &&
                    (niegaAlergias(paciente.alergias) ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Alergias: ${paciente.alergias}`}
                      />
                    ) : (
                      <Tooltip title={`Alergias: ${paciente.alergias}`}>
                        <Chip
                          size="small"
                          color="error"
                          icon={<WarningAmberRoundedIcon />}
                          label={`Alergias: ${paciente.alergias}`}
                          sx={{ maxWidth: { xs: '100%', md: 360 } }}
                        />
                      </Tooltip>
                    ))}
                </Stack>
              </>
            )}
          </Box>

          {paciente && (
            <Stack direction="row" sx={{ gap: '8px', flexShrink: 0, mt: '2px' }}>
              {puede('pacientes:editar') && !editando && (
                <Tooltip title="Editar datos de la paciente">
                  <Button
                    component={EnlaceRuta}
                    to={`${base}/editar`}
                    variant="outlined"
                    size="small"
                    aria-label="Editar datos de la paciente"
                    startIcon={<EditRoundedIcon />}
                    sx={{
                      minWidth: 0,
                      '& .MuiButton-startIcon': { mr: { xs: 0, md: '2px' } },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                      Editar datos
                    </Box>
                  </Button>
                </Tooltip>
              )}
              {puede('colposcopias:crear') &&
                resto !== 'estudios/nueva' &&
                !resto.endsWith('/corregir') && (
                  <Button
                    component={EnlaceRuta}
                    to={`${base}/estudios/nueva`}
                    // Una sola accion principal por pantalla: en las pestanas que
                    // traen la suya (firmar la historia, emitir receta) esta cede.
                    variant={resto === '' ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<AddRoundedIcon />}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Nueva colposcopía
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Estudio
                    </Box>
                  </Button>
                )}
            </Stack>
          )}
        </Stack>

        {paciente && pestanas.length > 1 ? (
          <Tabs
            value={vigente}
            aria-label="Secciones del expediente"
            sx={{
              mt: 'var(--space-5)',
              mx: 'calc(-1 * var(--density-card-p))',
              px: 'calc(var(--density-card-p) - 12px)',
              borderTop: '1px solid var(--border-hairline)',
              borderBottom: 0,
            }}
          >
            {pestanas.map((p) => (
              <Tab
                key={p.texto}
                value={p.ruta}
                label={p.texto}
                component={EnlaceRuta}
                to={p.ruta ? `${base}/${p.ruta}` : base}
              />
            ))}
          </Tabs>
        ) : (
          <Box sx={{ height: 'var(--density-card-p)' }} />
        )}
      </Box>
    </Box>
  );
}
